import React from "react";

import { ChangeCaseApplicability } from "@/components/governance/ChangeCaseApplicability";
import { ChangeCaseComparison } from "@/components/governance/ChangeCaseComparison";
import { ChangeCaseReviewActions } from "@/components/governance/ChangeCaseReviewActions";
import { ChangeCaseSubmit } from "@/components/governance/ChangeCaseSubmit";
import { DiscardChangeCase } from "@/components/governance/DiscardChangeCase";
import { GovernanceBones } from "@/components/governance/GovernanceBones";
import { useChangeCaseGovernance } from "@/components/governance/useChangeCaseGovernance";
import { humanizeRevisionPath } from "@/components/shared/diff-labels";
import {
  amendmentLabel,
  changeCaseKindLabel,
  changeCaseKindNoun,
  changeCaseStatusLabel,
  identityImpactLabel,
} from "@/components/shared/governance-labels";
import {
  Alert,
  Badge,
  Button,
  Divider,
  Icon,
  MutedText,
  Panel,
  SelectField,
  TextAreaField,
} from "@/components/ui";
import { FormField, TextField } from "@/components/ui/Form";
import type { EditorProgrammeMetadata, ProgrammePlatformClient, SessionContext } from "@/platform";
import type { ReadinessBlockerTarget } from "@/server/types";

export function ChangeCaseGovernancePanel({
  client,
  session,
  programme,
  changeCaseId,
  hasUnsavedChanges,
  onChanged,
  onReplacementCreated,
  onDiscarded,
  hrefFor,
}: {
  client: ProgrammePlatformClient;
  session: SessionContext;
  programme: EditorProgrammeMetadata;
  changeCaseId: string;
  hasUnsavedChanges: boolean;
  onChanged: () => Promise<void>;
  onReplacementCreated: (programmeId: string) => Promise<void>;
  /** The case is gone; leave its workspace. */
  onDiscarded: () => Promise<void>;
  /** The editor address a readiness blocker is cleared at, or null for none. */
  hrefFor: (target: ReadinessBlockerTarget) => string | null;
}): React.ReactElement {
  const {
    changeCase,
    comparison,
    loading,
    error,
    setError,
    reviewNote,
    setReviewNote,
    identityOutcome,
    setIdentityOutcome,
    identityRationale,
    setIdentityRationale,
    effectiveFrom,
    setEffectiveFrom,
    nextRevalidationDue,
    setNextRevalidationDue,
    replacementProgrammeCode,
    setReplacementProgrammeCode,
    exportLocked,
    isPending,
    busy,
    run,
    createReplacement,
    approve,
    refresh,
    isLead,
    isReviewer,
    isClientAdmin,
    identityChanges,
    approveRefusal,
    submissionBlockers,
    toQa,
  } = useChangeCaseGovernance({
    client,
    session,
    programme,
    changeCaseId,
    onChanged,
    onReplacementCreated,
  });

  const [discarding, setDiscarding] = React.useState(false);

  // Every write here refuses the same way while another is in flight.
  const refusal = (action: string, missing?: string): string | undefined =>
    isPending(action) ? "Wait for the change in progress to finish." : missing;

  if (loading && !changeCase) {
    return (
      <Panel tone="inset" className="my-3">
        <div role="status" aria-live="polite">
          <span className="sr-only">Reading the change case</span>
          <GovernanceBones />
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      tone="inset"
      headingLevel={2}
      className="my-4"
      data-testid="change-case-governance"
      title={`${changeCaseKindLabel(changeCase?.kind ?? programme.changeCaseKind)} workflow`}
      description={
        toQa
          ? "Submit this working revision directly for a Client Admin decision."
          : "Submit this working revision, complete internal review, then record the Client Admin decision."
      }
      actions={
        changeCase ? (
          <div className="flex flex-wrap gap-2">
            {changeCase.kind === "amendment" ? (
              <Badge variant="outline">{amendmentLabel(changeCase.classification)}</Badge>
            ) : null}
            <Badge variant="outline">{toQa ? "Direct QA approval" : "Full review"}</Badge>
            <Badge tone="primary">{changeCaseStatusLabel(changeCase.status)}</Badge>
          </div>
        ) : null
      }
    >
      {changeCase?.rationale ? (
        <MutedText className="max-w-3xl">
          <span className="font-medium">Opening rationale:</span> {changeCase.rationale}
        </MutedText>
      ) : null}

      {error ? (
        <Alert tone="error" className="mt-3">
          <span className="flex flex-wrap items-center gap-3">
            <span>{error.message}</span>
            {changeCase ? null : (
              <Button variant="ghost" size="sm" onClick={() => refresh()}>
                Try again
              </Button>
            )}
          </span>
        </Alert>
      ) : null}

      {changeCase &&
      (changeCase.status === "open" || changeCase.status === "changes-required") &&
      isLead ? (
        <ChangeCaseSubmit
          toQa={toQa}
          pending={isPending("submit")}
          hasUnsavedChanges={hasUnsavedChanges}
          blockers={submissionBlockers}
          hrefFor={hrefFor}
          onSubmit={() =>
            run("submit", () =>
              client.submitChangeCaseForReview({
                programmeId: programme.id,
                changeCaseId: changeCase.id,
                expectedRevision: programme.saveRevision,
              }),
            )
          }
        />
      ) : null}

      {comparison ? <ChangeCaseComparison changes={comparison.changes} /> : null}

      {changeCase?.status === "submitted" && isReviewer ? (
        <ChangeCaseReviewActions
          client={client}
          programmeId={programme.id}
          changeCaseId={changeCase.id}
          reviewNote={reviewNote}
          setReviewNote={setReviewNote}
          busy={busy}
          isPending={isPending}
          run={run}
        />
      ) : null}

      {changeCase?.status === "awaiting-approval" && isClientAdmin ? (
        <div className="mt-4 grid gap-4">
          <Divider className="col-span-full" />
          <ChangeCaseApplicability
            changeCase={changeCase}
            busy={busy}
            effectiveFrom={effectiveFrom}
            onEffectiveFromChange={setEffectiveFrom}
          />
          {identityChanges.length > 0 && !changeCase.identityImpactDecision ? (
            <div className="grid gap-3" data-testid="identity-impact-decision">
              <Alert tone="warning">
                Protected programme identity fields changed:{" "}
                {identityChanges.map((change) => humanizeRevisionPath(change.field)).join(", ")}.
              </Alert>
              <FormField label="Identity decision" htmlFor="identity-impact-outcome">
                <SelectField
                  id="identity-impact-outcome"
                  value={identityOutcome}
                  onChange={(next) => setIdentityOutcome(next as typeof identityOutcome)}
                  options={[
                    {
                      value: "continue-same-programme",
                      label: "Continue as the same programme",
                    },
                    {
                      value: "create-replacement-programme",
                      label: "Create a replacement programme",
                    },
                  ]}
                  disabled={busy}
                  disabledReason="Wait for the change in progress to finish."
                  data-testid="identity-impact-outcome"
                />
              </FormField>
              <FormField label="Decision rationale" htmlFor="identity-impact-rationale">
                <TextAreaField
                  id="identity-impact-rationale"
                  value={identityRationale}
                  onChange={setIdentityRationale}
                  disabled={busy}
                  disabledReason="Wait for the change in progress to finish."
                  data-testid="identity-impact-rationale"
                />
              </FormField>
              <Button
                variant="primary"
                size="sm"
                disabled={isPending("record-identity-impact") || !identityRationale.trim()}
                disabledReason={refusal("record-identity-impact", "Write the rationale first.")}
                onClick={() =>
                  run("record-identity-impact", () =>
                    client.decideChangeCaseIdentityImpact({
                      programmeId: programme.id,
                      changeCaseId: changeCase.id,
                      outcome: identityOutcome,
                      rationale: identityRationale.trim(),
                    }),
                  )
                }
                data-testid="record-identity-impact"
              >
                Record identity decision
              </Button>
            </div>
          ) : null}
          {changeCase.identityImpactDecision ? (
            <Alert tone="info">
              Identity decision: {identityImpactLabel(changeCase.identityImpactDecision.outcome)}.{" "}
              {changeCase.identityImpactDecision.rationale}
            </Alert>
          ) : null}
          {changeCase.identityImpactDecision?.outcome === "create-replacement-programme" ? (
            <div className="grid gap-3" data-testid="replacement-programme-form">
              <Alert tone="warning">
                The reviewed content will become a new programme linked to this one. It will retain
                the revalidation team and return to development before following the normal approval
                process.
              </Alert>
              <FormField label="New programme code" htmlFor="replacement-programme-code">
                <TextField
                  id="replacement-programme-code"
                  value={replacementProgrammeCode}
                  onChange={setReplacementProgrammeCode}
                  disabled={busy}
                  disabledReason="Wait for the change in progress to finish."
                  data-testid="replacement-programme-code"
                />
              </FormField>
              <div>
                <Button
                  variant="primary"
                  disabled={isPending("create-replacement") || !replacementProgrammeCode.trim()}
                  disabledReason={refusal("create-replacement", "Give the programme a code first.")}
                  onClick={() => createReplacement()}
                  data-testid="create-replacement-programme"
                >
                  <Icon name="git-branch" aria-hidden />
                  Create replacement programme
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
              <FormField
                label="Next revalidation due (optional)"
                htmlFor="change-case-next-revalidation"
              >
                <TextField
                  id="change-case-next-revalidation"
                  type="date"
                  value={nextRevalidationDue}
                  onChange={setNextRevalidationDue}
                  disabled={busy}
                  disabledReason="Wait for the change in progress to finish."
                  data-testid="change-case-next-revalidation"
                />
              </FormField>
              <Button
                variant="primary"
                disabled={approveRefusal !== undefined}
                disabledReason={approveRefusal}
                onClick={() => approve()}
                data-testid="approve-change-case"
              >
                Approve {changeCaseKindNoun(changeCase.kind)}
              </Button>
            </div>
          )}
        </div>
      ) : null}

      {changeCase?.status === "approved" ? (
        <Alert tone="success" className="mt-4">
          This {changeCaseKindNoun(changeCase.kind)} has been approved as a locked curriculum
          revision.
        </Alert>
      ) : null}
      {changeCase?.status === "replacement-created" ? (
        <Alert tone="success" className="mt-4">
          The replacement programme has been created and linked to this programme.
        </Alert>
      ) : null}

      {changeCase && (isLead || isClientAdmin) ? (
        <div className="mt-4 flex justify-end empty:hidden">
          <DiscardChangeCase
            changeCase={changeCase}
            exportLocked={exportLocked}
            busy={busy || discarding}
            busyReason="Wait for the change in progress to finish."
            onDiscard={() => {
              // Not through `run`: it re-reads the workspace, which is gone.
              setDiscarding(true);
              client
                .discardChangeCase({ programmeId: programme.id, changeCaseId: changeCase.id })
                .then(onDiscarded)
                .catch((cause: unknown) => {
                  setDiscarding(false);
                  setError(
                    cause instanceof Error ? cause : new Error("Unable to discard the change"),
                  );
                });
            }}
            onWithdraw={() => {
              run("withdraw", () =>
                client.withdrawChangeCase({
                  programmeId: programme.id,
                  changeCaseId: changeCase.id,
                }),
              );
            }}
          />
        </div>
      ) : null}
    </Panel>
  );
}
